import React, { useState } from "react";
import { getFileTypeByUrl } from "@web-src/utils/helper";
import { InteractiveContainer, PlainButton, Tooltip } from "@jugl-web/ui-kit";
import { getFileImagePath } from "@jugl-web/domain-resources/tasks/components/TaskAttachments/utils";
import ImageEditor from "@web-src/modules/media/components/ImageEditor/ImageEditor";
import { useTranslations } from "@jugl-web/utils";
import { MentionTextareaProps } from "@jugl-web/domain-resources/common/components/RichTextarea";
import { FilePreviewImage } from "@jugl-web/domain-resources/common/components/FilePreviewImage";
import { ReactComponent as DeleteIcon } from "./assets/delete.svg";
import { ReactComponent as PlayIcon } from "./assets/play.svg";
import { ReactComponent as PlusIcon } from "./assets/plus.svg";
import { ReactComponent as PenIcon } from "./assets/pen.svg";

export const ChatInputFilePreview: React.FC<{
  attachments: { file: File; caption: string }[];
  isLoading: boolean;
  canAddMoreAttachments: boolean;
  mentions?: MentionTextareaProps["mentions"];
  hideCaptionInput?: boolean;
  onRemoveClick: (index: number) => void;
  onEdit: ({
    index,
    file,
    caption,
  }: {
    index: number;
    file: File;
    caption: string;
  }) => void;
  onAddAttachment: () => void;
}> = ({
  attachments,
  canAddMoreAttachments,
  isLoading,
  mentions,
  hideCaptionInput,
  onRemoveClick,
  onAddAttachment,
  onEdit,
}) => {
  const { t } = useTranslations();
  const [editingAttachment, setEditingAttachment] = useState<{
    index: number;
    file: File;
    caption: string;
  } | null>(null);
  return (
    <>
      <ImageEditor
        isOpen={!!editingAttachment}
        onClose={() => setEditingAttachment(null)}
        hideCaptionInput={hideCaptionInput}
        mentions={mentions}
        onEdit={(update) => {
          if (!editingAttachment) return;
          setEditingAttachment({ ...editingAttachment, ...update });
        }}
        onSave={() => {
          if (!editingAttachment) return;
          onEdit(editingAttachment);
        }}
        onDelete={
          editingAttachment
            ? () => {
                onRemoveClick(editingAttachment.index);
                setEditingAttachment(null);
              }
            : undefined
        }
        {...editingAttachment}
      />
      {!!attachments.length && (
        <div className="jugl__custom-scrollbar-horizontal mb-1.5 flex items-center gap-[14px] overflow-x-auto pb-1.5">
          {attachments.map(({ file, caption }, index) => {
            const fileType = file && getFileTypeByUrl(file?.name);
            const isEditable = fileType === "image";
            return (
              <div
                className="group relative flex h-[120px] w-[120px] shrink-0 flex-col items-center justify-center gap-2 overflow-hidden rounded-lg border border-solid border-grey-200 bg-gradient-to-b from-white to-[#CDE2F2] select-none"
                key={+index}
              >
                {isEditable && !isLoading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-dark/20 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                    <Tooltip
                      placement="bottom"
                      renderTrigger={({ props, ref }) => (
                        <InteractiveContainer
                          {...props}
                          ref={ref}
                          className="flex h-8 w-8 transform cursor-pointer items-center justify-center rounded-full border-none bg-dark/50 transition-colors duration-200 hover:bg-dark/70"
                          onClick={() =>
                            setEditingAttachment({ file, caption, index })
                          }
                        >
                          <PenIcon />
                        </InteractiveContainer>
                      )}
                    >
                      {t({
                        id: "common.edit",
                        defaultMessage: "Edit",
                      })}
                    </Tooltip>
                  </div>
                )}
                {(() => {
                  if (fileType === "image") {
                    return <FilePreviewImage file={file} isImage />;
                  }

                  if (fileType === "video") {
                    return (
                      <>
                        <FilePreviewImage file={file} />
                        <PlayIcon className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />{" "}
                      </>
                    );
                  }

                  return (
                    <>
                      <img
                        src={getFileImagePath(file.name, "sm")}
                        alt={file?.name}
                        className="max-h-10 max-w-10"
                      />
                      <span className="w-full truncate p-2 text-center font-secondary text-xs text-dark-800">
                        {file.name}
                      </span>
                    </>
                  );
                })()}
                {!isLoading && (
                  <PlainButton
                    className="pointer-events-auto absolute top-0 right-0 flex h-8 w-8 items-center justify-center rounded-tr-lg rounded-bl-[14px] bg-dark/20"
                    onClick={() => onRemoveClick(index)}
                  >
                    <DeleteIcon />
                  </PlainButton>
                )}
              </div>
            );
          })}
          {canAddMoreAttachments && (
            <InteractiveContainer
              className="flex h-[120px] w-[120px] shrink-0 items-center justify-center rounded-lg border border-solid border-grey-200 bg-grey-200 transition-colors hover:bg-grey-300"
              onClick={onAddAttachment}
            >
              <PlusIcon />
            </InteractiveContainer>
          )}
        </div>
      )}
    </>
  );
};
